<template>
    <div>
        <div
            v-if="canOrder"
            class="d-flex align-items-center">
            <pc-label
                v-bind:for-id="orderById"
                class="me-2 text-nowrap"
                v-bind:label="orderByLabel" />
            <pc-select
                v-bind:id="orderById"
                v-model="sortColumnIndex"
                v-bind:options="colSortOptions" />
            <div
                v-bind:class="sortWidgetClass"
                class="sortable-header pe-4 ms-3"
                v-on:click="sortClick(sortColumnIndex)">
                &zwnj;
            </div>
        </div>
        <div
            v-for="(item, itemIndex) in internalItems"
            v-bind:id="item.id"
            v-bind:key="itemIndex"
            v-bind:class="getCardClass(itemIndex)"
            data-test-role="table-card-row"
            class="card my-2"
            v-on:click="setFocus(itemIndex, $event)">
            <div v-bind:class="headerClass">
                <h5
                    data-test-role="card-header-column-name"
                    class="card-title my-2">
                    <div v-bind:data-test-role="`cell_${headerColumn.property}`">
                        <slot
                            v-bind:name="headerColumn.property"
                            v-bind="item.item" />
                    </div>
                </h5>
                <div
                    v-if="showKebab"
                    class="dropdown d-flex">
                    <button
                        type="button"
                        class="btn border-0 py-0 shadow-none"
                        data-bs-toggle="dropdown">
                        <pc-bs-icon
                            name="three-dots"
                            tabindex="0" />
                    </button>
                    <slot
                        name="contextMenu"
                        v-bind="item.item" />
                </div>
            </div>
            <div
                v-if="showBody"
                class="card-body">
                <div>
                    <div
                        v-for="(column, colIndex) in cardColumns"
                        v-bind:key="getCellKey(itemIndex, colIndex, item)"
                        v-on:click="cellClick(colIndex + 1, item)">
                        <div v-if="!hideColumnNames">
                            <strong data-test-role="card-column-name">
                                <pc-label
                                    v-bind:label="column.header"
                                    v-bind:show-required="column.showRequired"
                                    v-bind:show-help="column.showHelp"
                                    v-on:help-clicked="helpClick(column.originalIndex, $event)" />
                            </strong>
                        </div>
                        <div v-bind:data-test-role="`cell_${column.property}`">
                            <slot
                                v-bind:name="column.property"
                                v-bind="item.item" />
                        </div>
                        <hr
                            v-if="!hideHorizontalSeparator"
                            class="my-2" />
                    </div>
                    <transition
                        v-on:enter="enter"
                        v-on:leave="leave">
                        <div v-if="item.expanded && hasMoreColumns">
                            <div
                                v-for="(column, index) in moreColumns"
                                v-bind:key="index"
                                v-on:click="cellClick(index + cardColumns.length + 1, item)">
                                <div v-if="!hideColumnNames">
                                    <strong>
                                        <pc-label
                                            v-bind:label="column.header"
                                            v-bind:show-required="column.showRequired" />
                                    </strong>
                                </div>
                                <div v-bind:data-test-role="`cell_${column.property}`">
                                    <slot
                                        v-bind:name="column.property"
                                        v-bind="item.item" />
                                </div>
                                <hr class="my-2" />
                            </div>
                        </div>
                    </transition>
                    <transition
                        v-on:enter="enter"
                        v-on:leave="leave">
                        <div
                            v-if="!showKebab && showActions(item.item) && item.expanded"
                            data-test-role="table-expanded-row"
                            data-test-state="expanded"
                            class="card-margin-top-1spacer">
                            <!-- Note: Must use custom class; bootstrap class
                            doesn't play nice with jQuery slide toggle bc it uses
                            !important -->
                            <slot
                                v-bind="item.item"
                                name="actionsRow" />
                        </div>
                    </transition>
                    <transition
                        v-on:enter="enter"
                        v-on:leave="leave">
                        <div
                            v-if="!item.expanded && (hasMoreColumns || showActions(item.item))"
                            class="text-body-secondary fst-italic card-margin-top-1spacer">
                            <!-- Note: Must use custom class; bootstrap class
                            doesn't play nice with jQuery slide toggle bc it uses
                            !important -->
                            {{ expandableText }}
                        </div>
                    </transition>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import commonOptions from "./_common.js";
    import { KEYS, NAMESPACE } from "./pc-table.i18n";
    import { mapI18n } from "Util/i18n";

    export default {
        extends: commonOptions,
        props: {
            hideColumnNames: { type: Boolean, required: false, default: false },
            hideSort: { type: Boolean, required: false, default: false },
            propertyCutoff: { type: Number, required: true },
            primarySortIndex: { type: Number, required: true },
            hideActions: { type: Boolean, required: false, default: false },
            hideHorizontalSeparator: { type: Boolean, required: false, default: false },
            showKebab: { type: Boolean, required: false, default: false }
        },
        data: function () {
            const internalItems = this._calcInternalItems();
            const orderById = this.$idGenerator.getNextId("pcTable_cardOrderBy");
            return {
                internalItems,
                orderById
            };
        },
        computed: {
            showBody() {
                return (
                    this.cardColumns?.length > 0 ||
                    Object.values(this.internalItems).some((i) => this.showActions(i?.item))
                );
            },
            headerClass() {
                return {
                    "card-header": this.showBody,
                    "card-body": !this.showBody,
                    "bg-transparent": true,
                    "d-flex": true,
                    "align-items-center": true,
                    "justify-content-between": true
                };
            },
            headerColumn() {
                const column = this.columns.find((c) => c.isCardHeader);
                return column || this.columns[0];
            },
            nonHeaderColumns() {
                const filtered = [];
                let filteredIndex = 0;
                for (let index = 0; index < this.columns.length; index++) {
                    if (this.columns[index].visible && this.columns[index] !== this.headerColumn) {
                        filtered.push({ ...this.columns[index] });
                        filtered[filteredIndex].originalIndex = index;
                        filteredIndex++;
                    }
                }
                return filtered;
            },
            cardColumns() {
                const cols = this.nonHeaderColumns.slice(0, this.propertyCutoff);
                return cols;
            },
            moreColumns() {
                const cols = this.nonHeaderColumns.slice(this.propertyCutoff);
                return cols;
            },
            showMoreBtn() {
                return this.moreColumns.length > 0;
            },
            colSortOptions() {
                // Filter non-orderable after mapping, otherwise indices of options
                // won't match indices of internal columns
                return this.columns
                    .map((c, i) => {
                        return c.orderable ? { text: c.header, value: i } : null;
                    })
                    .filter((option) => {
                        return option != null;
                    });
            },
            sortColumnIndex: {
                get() {
                    return this.primarySortIndex || 0;
                },
                set(value) {
                    this.sortClick(value);
                }
            },
            sortWidgetClass() {
                return this.getClassObject(this.columns[this.sortColumnIndex]);
            },
            hasMoreColumns() {
                return this.moreColumns.length > 0;
            },
            expandableText() {
                if (this.hasMoreColumns) {
                    const nMoreCols = this.moreColumns.length;

                    // Even though these are data columns, in the UI they're laid
                    // out horizontally so rows makes more sense
                    const colText = nMoreCols === 1 ? "row" : "rows";
                    return `${nMoreCols} more ${colText}`;
                }
                return "Additional actions...";
            },
            canOrder() {
                return !this.hideSort && this.colSortOptions.length > 0;
            },
            ...mapI18n(KEYS, NAMESPACE)
        },
        watch: {
            items: {
                handler() {
                    this.internalItems = this._calcInternalItems();
                },
                deep: true
            }
        },
        methods: {
            itemAtIndex(index) {
                return this.items[index];
            },
            itemHasFocus(index) {
                return this.itemAtIndex(index).expanded;
            },
            _calcInternalItems() {
                return this.items.map((i) => ({
                    item: i.item,
                    expanded: i.expanded,
                    id: i.id
                }));
            },
            setFocus(index, $event) {
                const nodeName = $event.target.nodeName;
                const wasInputClicked =
                    nodeName === "BUTTON" ||
                    nodeName === "INPUT" ||
                    nodeName === "SELECT" ||
                    nodeName === "LABEL" ||
                    nodeName === "svg";
                if (!wasInputClicked) {
                    this.rowClick(index);
                }
            },
            cardBaseClass(index) {
                return this.hasMoreColumns || this.showActions(this.itemAtIndex(index).item) ? "card-clickable" : "";
            },
            getCardClass(index) {
                const base = this.cardBaseClass(index);
                if (this.itemHasFocus(index)) {
                    return `${base} focused-card`;
                } else {
                    return base;
                }
            }
        }
    };
</script>

<style lang="scss" scoped>
    .card-margin-top-1spacer {
        margin-top: 1rem;
    }

    .card-clickable:hover {
        cursor: pointer;
    }

    .focused-card {
        background-color: var(--bs-primary-200);
    }

    .sortable-header {
        position: relative;
        cursor: pointer;
    }
    .sortable-header:hover {
        background-color: var(--bs-gray-100);
    }
    .sortable-header:before,
    .sortable-header:after {
        position: absolute;
        top: 0;
        display: block;
        opacity: 0.4;
        padding-top: inherit;
        padding-bottom: inherit;
        font-size: inherit;
    }
    .sortable-header:before {
        right: 0.75em;
        content: "\25B2"; /*unicode for triangle up*/
    }
    .sortable-header.sorting-asc:before {
        opacity: 1;
    }
    .sortable-header.sorting-desc:after {
        opacity: 1;
    }
    .sortable-header:after {
        right: 0.25em;
        content: "\25BC"; /*unicode for triangle down*/
    }
</style>
